import { AnimatePresence } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'

import { OrderAmount } from '@entities/Order'

import { ClearCart } from '@features/cart'
import { useCartStore } from '@features/cart/model/useCartStore'
import { ProductCarouselNew } from '@features/products-carousel'

import CartBottomBar from '@widgets/cart-bottom-bar'
import { EmptyCart } from '@widgets/cart-empty'
import { CartEmptyInfo } from '@widgets/cart-empty-info'
import { ProductCart, ProductCartCardProps } from '@widgets/product-cart'

import { URL_CHECKOUT } from '@shared/contants'
import { useAnalytics } from '@shared/lib/analytics'
import BackButton from '@shared/ui/back-button'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Wrapper from '@shared/ui/wrapper'
import getNoun from '@shared/uttils/getNoun'

import styles from './styles.module.scss'

function SkeletonLoading () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={3}
        width={'100%'}
        height={160}
        style={{
          borderRadius: 6,
          marginBottom: 20,
        }}
      />
    </SkeletonTheme>
  )
}

export default function CartPage () {
  const navigate = useNavigate()
  const { t: tp } = useTranslation('product')
  const { t: tc } = useTranslation('cart')
  const {
    getTotalQuantity,
    getCart,
    getProduct,
    checkProductsInStock,
    cartInfoData,
    cartInfoLoading,
    isSyncing,
  } = useCartStore()
  const { viewCart: trackViewCart, trackEvent } = useAnalytics()
  const hasTrackedViewCart = useRef(false)

  const orderRef = useRef<HTMLDivElement>(null)
  const [ orderInView, setOrderInView ] = useState(false)
  const PRODUCT_COUNT = getTotalQuantity()
  const cart = getCart()

  const TOTAL_SUM = useMemo(() => {
    return cartInfoData?.totals?.total
  }, [ cartInfoData ])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([ entry ]) => setOrderInView(entry.isIntersecting),
      { threshold: 0.1 },
    )
    const currentRef = orderRef.current
    if (currentRef) observer.observe(currentRef)
    return () => {
      if (currentRef) observer.unobserve(currentRef)
    }
  }, [])

  useEffect(() => {
    checkProductsInStock()
  }, [ checkProductsInStock ])
  

  const handleCheckout = (_paymentId: string) => {
    trackEvent('go_to_checkout_click', {
      source: 'cart_page',
      items_count: PRODUCT_COUNT,
      cart_total: TOTAL_SUM,
    })
    navigate(URL_CHECKOUT)
  }

  const LOADING = useMemo(() => {
    return cartInfoLoading || isSyncing
  }, [ cartInfoLoading, isSyncing ])

  const IN_STOCK_PRODUCT = useMemo(() => {
    const products = cartInfoData?.products
    if (!products || !cart?.items) return []

    return products.filter((p) => {
      const amount = cart.items.find((i) => i.product_id === p?.id)?.amount
      return amount && amount > 0
    })
  }, [ cartInfoData?.products, cart?.items ])

  const OUT_OF_STOCK_PRODUCTS = useMemo(() => {
    const products = cartInfoData?.products
    if (!products || !cart?.items) return []

    return products.filter((p) => {
      const amount = cart.items.find((i) => i.product_id === p?.id)?.amount
      return amount === 0
    })
  }, [ cartInfoData?.products, cart?.items ])

  const hasOutOfStock = OUT_OF_STOCK_PRODUCTS.length > 0

  useEffect(() => {
    if (!hasTrackedViewCart.current && IN_STOCK_PRODUCT.length && TOTAL_SUM !== undefined) {
      const validProducts = IN_STOCK_PRODUCT.filter((p): p is NonNullable<typeof p> => p !== null)
      trackViewCart(validProducts)
      hasTrackedViewCart.current = true
    }
  }, [ IN_STOCK_PRODUCT, TOTAL_SUM, trackViewCart ])

  return (
    <div className={styles.root}>
      <div className={styles.root__container}>
        <div className={styles.root__left}>
          <Wrapper>
            <div className={styles.root__grid}>
              <Breadcrumbs
                links={[
                  {
                    title: tc('title'),
                  },
                ]}
              />

              {!LOADING && (
                <div className={styles.root__top}>
                  <BackButton />
                  <div className={styles.root__header}>
                    <h1 className={styles.root__title}>
                      {tc('title')}
                      {PRODUCT_COUNT && (
                        <span className={styles.root__count}>
                          {getTotalQuantity()}{' '}
                          {getNoun(
                            getTotalQuantity(),
                            tp('product.1'),
                            tp('product.2'),
                            tp('product.3'),
                          )}
                        </span>
                      )}
                    </h1>
                  </div>
                </div>
              )}

              {LOADING && <SkeletonLoading />}

              {!PRODUCT_COUNT && <EmptyCart />}

              {hasOutOfStock && !LOADING && (
                <div className={styles.root__warning}>
                  {tc('some_items_unavailable') || 'Некоторые товары недоступны для заказа. Вы можете их заменить или удалить из корзины.'}
                </div>
              )}

              {IN_STOCK_PRODUCT &&
                !!IN_STOCK_PRODUCT.length &&
                PRODUCT_COUNT &&
                !LOADING && (
                <div className={styles.root__group}>
                  <div className={styles['root__group-top']}>
                    <h2 className={styles['root__group-title']}>
                      {tc('available')}
                    </h2>
                    <ClearCart />
                  </div>
                  <div className={styles.root__products}>
                    {IN_STOCK_PRODUCT &&
                        IN_STOCK_PRODUCT.map((p) => {
                          const product = getProduct(p?.id as string)
                          const amount = product?.amount as number

                          return (
                            <div
                              className={styles.root__item}
                              key={p?.id as string}
                            >
                              <ProductCart
                                data={p?.info as ProductCartCardProps['data']}
                                amount={amount}
                              />
                            </div>
                          )
                        })}
                  </div>
                </div>
              )}

              {PRODUCT_COUNT && (
                <div className={styles.root__carousel}>
                  <ProductCarouselNew />
                </div>
              )}
            </div>
          </Wrapper>
        </div>

        <div className={styles.root__right} ref={orderRef}>
          {PRODUCT_COUNT ? (
            <OrderAmount
              onSubmit={handleCheckout}
              loading={LOADING}
              showPaymentControls={false}
              showDelivery={false}
            />
          ) : (
            <Wrapper>
              <CartEmptyInfo />
            </Wrapper>
          )}
        </div>
      </div>
      <AnimatePresence>
        {PRODUCT_COUNT && !orderInView && TOTAL_SUM && (
          <CartBottomBar total={TOTAL_SUM} />
        )}
      </AnimatePresence>
    </div>
  )
}
